// Articles — the little words (o, a, um, uma…) that agree with their noun,
// the contractions with prepositions (do, no, ao, pelo…), and the cases where
// Portuguese uses an article that English drops (and vice versa). A forms table closes it.
import { renderGuideSections } from "../ui/guideSections.js";

export function articlesView(app) {
  const table = `
    <div class="bg-white rounded-2xl border border-sage-100 p-5 mt-6">
      <h3 class="text-lg font-bold text-sage-800 mb-3">Article Forms</h3>
      <div class="overflow-x-auto"><table class="w-full text-sm">
        <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
          <th class="py-2 pr-3"></th>
          <th class="py-2 px-3">Masc. sg</th>
          <th class="py-2 px-3">Fem. sg</th>
          <th class="py-2 px-3">Masc. pl</th>
          <th class="py-2 px-3">Fem. pl</th>
        </tr></thead>
        <tbody>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">Definite (the)</td><td class="py-2 px-3">o</td><td class="py-2 px-3">a</td><td class="py-2 px-3">os</td><td class="py-2 px-3">as</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">Indefinite (a / some)</td><td class="py-2 px-3">um</td><td class="py-2 px-3">uma</td><td class="py-2 px-3">uns</td><td class="py-2 px-3">umas</td></tr>
          <tr><td class="py-2 pr-3 font-medium">Contractions</td><td class="py-2 px-3" colspan="4"><b>de + o = do</b> · <b>em + a = na</b> · <b>a + o = ao</b> · <b>por + os = pelos</b></td></tr>
        </tbody>
      </table></div>
      <p class="text-sage-500 text-xs mt-3">Portuguese has no neuter article; instead, <b>o</b> + adjective can express an abstract idea: <i>o bom</i> (the good part), <i>o importante</i> (what's important).</p>
    </div>`;

  renderGuideSections(
    app,
    "Articles",
    "Articles are the small words in front of a noun — 'the', 'a', 'some'. In Portuguese they agree with the noun in gender and number, and they show up in a few places English would leave blank.",
    [
      {
        title: "1. Definite vs indefinite",
        body: `
          <p class="text-sage-700 mb-2"><b>Definite</b> (o/a/os/as) = a specific, known thing — English <i>the</i>. <b>Indefinite</b> (um/uma/uns/umas) = any one of a kind — English <i>a/an</i>; the plural <i>uns/umas</i> means <i>some / a few</i>.</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i><b>o</b> carro</i> = the (particular) car · <i><b>um</b> carro</i> = a car</li>
            <li><i><b>as</b> casas</i> = the houses · <i><b>umas</b> casas</i> = some houses</li>
          </ul>
        `,
      },
      {
        title: "2. Contractions — articles fuse with prepositions",
        body: `
          <p class="text-sage-700 mb-2">When <i>de, em, a</i> or <i>por</i> meets the article, they fuse. This happens across the board, not just in a couple of cases.</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>de + o = <b>do</b></i>, <i>de + a = <b>da</b></i>: <i>o livro <b>do</b> João</i>, <i>a casa <b>da</b> Ana</i>.</li>
            <li><i>em + o = <b>no</b></i>, <i>em + a = <b>na</b></i>: <i>Estou <b>no</b> mercado</i>, <i><b>na</b> cidade</i>.</li>
            <li><i>a + o = <b>ao</b></i>, <i>a + a = <b>à</b></i>: <i>Vou <b>ao</b> mercado</i>, <i><b>à</b> escola</i>.</li>
            <li><i>por + o = <b>pelo</b></i>, <i>por + a = <b>pela</b></i>: <i>passo <b>pelo</b> parque</i>.</li>
          </ul>
        `,
      },
      {
        title: "3. Articles with names and possessives",
        body: `
          <p class="text-sage-700 text-sm">European Portuguese normally puts the definite article before a person's first name (<i><b>a</b> Maria</i>, <i><b>o</b> João</i>) and before a possessive (<i><b>o</b> meu carro</i>, <i><b>a</b> minha casa</i>). Most countries take it too — <i><b>o</b> Brasil</i>, <i><b>a</b> França</i> — though a few drop it: <i>Portugal</i>, <i>Angola</i>.</p>
        `,
      },
      {
        title: "4. When Portuguese uses an article (and English doesn't)",
        body: `
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>General nouns:</b> <i><b>O</b> café é bom</i> = Coffee is good.</li>
            <li><b>Days:</b> <i><b>à</b> segunda</i> = on Mondays · <i><b>no</b> domingo</i> = on Sunday.</li>
            <li><b>Body parts & clothing</b> (not possessives): <i>Dói-me <b>a</b> cabeça</i> = My head hurts.</li>
            <li><b>Titles (talking about):</b> <i><b>O</b> senhor Silva</i> — but drop it when addressing them.</li>
          </ul>
          <p class="text-sage-700 text-sm mt-3"><b>Omit</b> the indefinite article before unmodified professions: <i>Sou professor</i> (I'm a teacher) — add it back if qualified: <i>Sou <b>um</b> professor excelente</i>.</p>
        `,
      },
    ],
    "",
    table
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
